import angular from 'angular';
import _ from 'lodash-es';
import APIV5 from '~/models/api';
import Defaults from '~/scripts/constants/defaults';
import Workflow from '~/scripts/services/model/workflow';
import WorkflowNodeAction from '~/scripts/services/model/workflow-node-action';
import Card from '~/scripts/services/model/card';

/**
 * @typedef WfNodeSelectOptionsListItem
 * @property {?import('@knitiv/api-client-javascript').KNode[]} [exclude]
 * @property {?import('@knitiv/api-client-javascript').KNode} [default]
 * @property {?import('@knitiv/api-client-javascript').KNode[]} [eligible]
 * @property {?boolean} [required=false]
 */

/**
 * @typedef WfNodeSelectOptions
 * @property {?WfNodeSelectOptionsListItem[]} [list]
 * @property {?import('@knitiv/api-client-javascript').KNode} [node]
 */

export default class WfNodeSelectController {
	/* @ngInject */
	/**
	 * @param {import('angular').auto.IInjectorService} $injector
	 * @param {any} $scope
	 * @param {import('angular').ITimeoutService} $timeout
	 * @param {import('angular').translate.ITranslateService} $translate
	 * @param {any} Notification
	 */
	constructor(
		$injector,
		$scope,
		$timeout,
		$translate,
		Notification,
	) {
		this.$injector = $injector;
		this.$scope = $scope;
		this.$timeout = $timeout;
		this.$translate = $translate;
		this.Notification = Notification;

		/** @type {any[]} */
		this.actions = [];

		/** @type {any[]} */
		this.data = [];

		/** @type {WfNodeSelectOptions} */
		this.defaultOptions = {
			list: [],
		};

		/** @type {?import('~/scripts/services/model/form').default} */
		this.form = null;

		this.index = undefined;

		/** @type {?import('~/components/kform/kform.controller').default} */
		this.kformCtrl = null;

		/** @type {import('~/components/mf/mf.controller').MfOptions} */
		this.mfAllocatorOptions = {
			displayAddBtn: false,
			displayValues: true,
			errors: {
				emptyResults: 'workflow.no-allocator',
			},
		};

		this.name = '';

		/** @type {?angular.INgModelController} */
		this.ngModelCtrl = null;

		/** @type {WfNodeSelectOptions} */
		this.options = {};

		this.state = undefined;

		/** @type {any} */
		this.tmp = {
			allocatorCard: '1-N',
			mfCustom:      {
				selectable: [
					'K_NODE;WF_ROLE',
					'K_NODE;KPROFIL',
				],
			},
			wfCard:        '1-1',
		};
	}

	/**
	 * @param {any} wf
	 */
	abort(wf) {
		if (!wf.workflowNodeAction) {
			wf.workflowNodeAction = new WorkflowNodeAction();
			this.actions.push(wf.workflowNodeAction);
		}
		wf.workflowNodeAction.action = 'WFAbort';
		wf.workflowNodeAction.wf_affect_kid = wf.wf_affect_kid;
	}

	async affect() {
		if (!Array.isArray(this.data)) {
			this.data = [];
		}

		this.data.push({
			allocator:   [],
			allocators:  [],
			commentaire: '',
			error:       false,
			kid:         null,
			state:       null,
			states:      [],
		});

		this.$timeout(() => {
			this.$scope.$apply();
		}, 100);
	}

	/**
	 * @param {number} index
	 * @param {import('@knitiv/api-client-javascript').KNode} wfKid
	 */
	delete(index, wfKid) {
		_.remove(this.actions, action => action.wf_kid === wfKid);
		this.data[index] = {
			allocator:   [],
			allocators:  [],
			commentaire: '',
			error:       false,
			state:       null,
			states:      [],
		};
		this.ngModelCtrl?.$setViewValue(this.data);
		this.ngModelCtrl?.$validate();
	}

	deleteAllocator() {
		this.ngModelCtrl?.$validate();
	}

	/**
	 * @param {any} item
	 * @param {any} wf
	 */
	async select(item, wf) {
		if (!wf) {
			return;
		}
		wf.state = null;
		wf.allocators.length = 0;
		wf.states.length = 0;
		wf.allocator.length = 0;

		if (item) {
			if (this.data.some(index => index.kid === item.kid)) {
				this.Notification.warning({
					title:   this.$translate.instant('error.title'),
					message: this.$translate.instant('error.wf-already-selected'),
				});
				this.$timeout(() => {
					wf.kid = null;
				});
			}
			else {
				wf.kid = item.kid;
				/** @type {any} */
				const w = await APIV5.instance.wfWorkflowLoad(
					{
						wf_kid: item.kid,
						initial: 1,
					}, {
						id: 'wf-node-select.select',
					});
				const workflow = Object.assign(new Workflow(this.$injector, item.kid), w);

				for (const state of workflow.states) {
					if (state.initial === 1) {
						wf.states.push({
							kid:  state.kid,
							name: state.getName(),
						});
					}
				}

				if (workflow.states.length === 1) {
					await this.stateSelect(workflow.states[0], wf);
				}
				else {
					this.ngModelCtrl?.$validate();
				}

				if (wf.states.length === 1) {
					wf.state = wf.states[0].kid;
				}

				this.$timeout(() => this.$scope.$apply());
			}
		}
	}

	/**
	 * @param {any} wf
	 */
	selectAllocator(wf) {
		this.$timeout(() => {
			if (this.actions && Array.isArray(this.actions)) {
				if (!wf.workflowNodeAction) {
					wf.workflowNodeAction = new WorkflowNodeAction();
					wf.workflowNodeAction.setScope(this.$scope, wf);
					this.actions.push(wf.workflowNodeAction);
				}
				wf.workflowNodeAction.action = 'WFAffect';
				wf.workflowNodeAction.node_kid = this.options.node;
				wf.workflowNodeAction.wf_kid = wf.kid;
				wf.workflowNodeAction.wfstate_kid = wf.state;
				wf.workflowNodeAction.wfallocatorlist = wf.allocator.map((/** @type {any} */ allocator) => allocator.kid);
				wf.workflowNodeAction.wfcomment = wf.wfcomment;
			}
			this.ngModelCtrl?.$validate();
		});
	}

	stateDelete() {
		this.$timeout(() => {
			this.ngModelCtrl?.$setViewValue(this.data);
			this.ngModelCtrl?.$validate();
		});
	}

	/**
	 * @param {any} wf
	 * @returns {boolean}
	 */
	stateGetName(wf) {
		const state = wf.states.filter((/** @type {any} */ s) => s.kid === wf.state);
		return state.length === 1 ? state[0].name : '';
	}

	/**
	 * @param {any} state
	 * @param {any} wf
	 */
	async stateSelect(state, wf) {
		wf.allocators.length = 0;
		wf.allocator.length = 0;
		this.ngModelCtrl?.$setDirty();
		this.ngModelCtrl?.$setTouched();
		if (state) {
			/** @type {any} */
			const result = await APIV5.instance.wfStateInitialInfo(
				{ wfstate_kid: state.kid },
				{ id: 'wf-node-select.state-select' },
			);
			wf.stateAffectAuto = result.wfstate.affectAuto;
			wf.allocators.push(...result.wfallocatorlist);

			if (result.wfstate?.kndef?.validate) {
				wf.allocatorCard = new Card(`${result.wfstate.kndef.validate.min ?? '1'}-`
					+ `${result.wfstate.kndef.validate.max && result.wfstate.kndef.validate.max !== 'N'
						? result.wfstate.kndef.validate.max : Defaults.KCARDN}`);
			}

			if (wf.stateAffectAuto) {
				wf.allocator.push(...wf.allocators);
				this.selectAllocator(wf);
			}

			this.$timeout(() => {
				this.$scope.$apply();
				this.ngModelCtrl?.$validate();
			});
		}
		else {
			this.ngModelCtrl?.$validate();
		}
	}

	$onInit() {
		this.options = {
			...this.defaultOptions,
			...this.options,
		};

		if (!Array.isArray(this.actions)) {
			this.actions = [];
		}

		this.$scope.$watch('$ctrl.options.list', async (/** @type {WfNodeSelectOptionsListItem[] | undefined} */ list) => {
			this.data.length = 0;
			if (Array.isArray(list)) {
				/** @type {Set<string>} */
				const items = new Set(list.flatMap(l => l.eligible?.map(wf => wf.toString()) ?? []));

				if (items) {
					const result = await APIV5.instance.repreListSimple({
						items: [...items],
						mode:  'simple',
					}, {
						id: 'wf-node-select.list-modified',
					});
					if (result?.result) {
						this.data.push(...list.map(l => ({
							allocator:     [],
							allocators:    [],
							commentaire:   '',
							default:       l.default,
							error:         false,
							kid:           undefined,
							list:          l.eligible?.map(wf => ({ kid: wf, name: result.result[wf] })),
							required:      l.required,
							state:         null,
							states:        [],
							wfCard:        new Card(l.required ? '1-1' : '0-1'),
							wfPlaceholder: l.required
								? this.$translate.instant('object.workflow')
								: this.$translate.instant('object.workflow-optional'),
						})));

						// check for default
						this.$timeout(async () => {
							for (const wf of this.data) {
								if (wf.default) {
									await this.select({ kid: wf.default }, wf);
								}
							}
						});
					}
				}
			}
		}, true);

		if (this.ngModelCtrl) {
			this.ngModelCtrl.$validators.stateRequired = (/** @type {any} */ modelValue) => {
				let valid = true;
				if (Array.isArray(modelValue)) {
					for (const wf of modelValue) {
						if (wf) {
							wf.error = false;
							if (wf.kid && !wf.state) {
								valid = false;
								wf.error = true;
							}
						}
					}
				}

				return valid;
			};

			this.ngModelCtrl.$validators.wfRequired = () => {
				let valid = Array.isArray(this.actions);
				for (const wf of this.data) {
					if (valid && wf?.required) {
						if (this.actions.length === 0) {
							valid = false;
						}
						else {
							for (const action of this.actions) {
								action.error = false;
								if (!action.wf_kid) {
									valid = false;
									action.error = true;
								}
							}
						}
					}
				}
				this.form?.formScope?.kformForm?.$setValidity('wfRequired', valid, this.form?.formScope?.kformForm);
				return valid;
			};

			this.ngModelCtrl.$validators.wfAllocatorsMin = () => {
				let valid = Array.isArray(this.actions);
				for (const wf of this.data) {
					if (valid) {
						for (const action of this.actions) {
							action.error = action.wf_kid ? action.wfallocatorlist.length < wf.allocatorCard.from : false;
						}
						valid = this.actions.every((/** @type {any} */ wf) => wf.error === false);
					}
				}

				this.form?.formScope?.kformForm?.$setValidity('wfAllocatorsMin', valid, this.form?.formScope?.kformForm);
				return valid;
			};

			this.ngModelCtrl.$validators.wfAllocatorsMax = () => {
				let valid = Array.isArray(this.actions);
				for (const wf of this.data) {
					if (valid) {
						for (const action of this.actions) {
							action.error = false;
							if (action.wfallocatorlist.length > wf.allocatorCard.to) {
								valid = false;
								action.error = true;
							}
						}
					}
				}

				this.form?.formScope?.kformForm?.$setValidity('wfAllocatorsMax', valid, this.form?.formScope?.kformForm);
				return valid;
			};
		}

		// this.options.wfAffect = this.affect;
		(async () => this.affect())();
	}
}
